import { z } from "zod"
import { DurationSchema, TimelineSchema, PositionSchema } from "./timing"
import { AICaptionsSchema, CaptionStyleConfigSchema, type CaptionSegment } from "./caption"
import type { HookSegment } from "./hook"
import type { EmojiSegment } from "./emoji"
import type { BRollSegment } from "./broll"
import type { MusicSegment } from "./music"
import type { VoiceoverSegment } from "./voiceover"
import type { GifSegment } from "./gif"
import type { ReframeSegment } from "./reframe"

// =============================================================================
// Segment Union Type
// =============================================================================

export type Segment =
  | CaptionSegment
  | HookSegment
  | EmojiSegment
  | BRollSegment
  | MusicSegment
  | VoiceoverSegment
  | GifSegment
  | ReframeSegment

// =============================================================================
// Track Types
// =============================================================================

export const TrackTypeSchema = z.enum([
  "CaptionTrack", "HookTrack", "BRollTrack", "MusicTrack",
  "VoiceoverTrack", "GifTrack", "EmojiTrack", "ReframeTrack",
  "KeyFrameTrack", "EffectsTrack",
])

export const SectionSchema = z.object({
  id: z.string(),
  sectionDuration: DurationSchema,
  sectionTimeline: TimelineSchema,
  segments: z.array(z.record(z.string(), z.any())),
  sceneId: z.string().nullable().default(null),
  propertiesMap: z.record(z.string(), z.unknown()).optional(),
})

export const TrackSchema = z.object({
  id: z.string(),
  trackType: TrackTypeSchema,
  visible: z.boolean().default(true),
  locked: z.boolean().default(false),
  sections: z.array(SectionSchema).default([]),
  styleConfig: CaptionStyleConfigSchema.nullable().default(null),
  aiCaptions: AICaptionsSchema.optional(),
})

// =============================================================================
// Render Preferences
// =============================================================================

export const RenderPreferencesSchema = z.object({
  enableCaption: z.boolean().default(true),
  enableBroll: z.boolean().default(true),
  enableHook: z.boolean().default(true),
  enableEmoji: z.boolean().default(false),
  enableMusic: z.boolean().default(false),
  enableVoiceover: z.boolean().default(false),
  enableGif: z.boolean().default(false),
  enableReframe: z.boolean().default(false),
  layoutAspectRatio: z.enum(["portrait", "landscape", "square"]).default("portrait"),
})

// =============================================================================
// Reframe Bounds (auto-reframe crop region)
// =============================================================================

export const ReframeBoundsSchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
  outputFormat: z.enum(["9:16", "1:1", "4:5", "16:9"]).default("9:16"),
  faceCount: z.number().default(0),
  sourceWidth: z.number().default(1920),
  sourceHeight: z.number().default(1080),
})

// =============================================================================
// Auto Hook
// =============================================================================

export const AutoHookTextOverlaySchema = z.object({
  hookText: z.string(),
})

export const AutoHookSchema = z.object({
  enabled: z.boolean(),
  textOverlay: AutoHookTextOverlaySchema.optional(),
  hookType: z.string().optional(),
  strengthScore: z.number().optional(),
})

// =============================================================================
// Scene Metadata (V4 per-chunk reframing)
// =============================================================================

// A face box at `t` seconds of the clip (frame fractions); the renderer eases between keyframes.
export const FaceKeyframeSchema = z.object({
  t: z.number(),
  x_pct: z.number(),
  y_pct: z.number(),
  w_pct: z.number(),
  h_pct: z.number(),
})

export const SceneFaceSchema = z.object({
  x_pct: z.number(),
  y_pct: z.number(),
  w_pct: z.number(),
  h_pct: z.number(),
  // Where this person is over the scene; absent = the static box above. A manual drag drops it.
  track: z.array(FaceKeyframeSchema).optional(),
})

// The analyzer's confirmation that every panel keeps its face: per-panel share of sampled frames with
// the face inside the crop, and whether it refit a panel or stepped the layout down (`from`).
export const LayoutCheckSchema = z.object({
  status: z.enum(["ok", "refit", "downgraded"]),
  coverage: z.array(z.number()),
  from: z.string().optional(),
})

export const SceneChunkSchema = z.object({
  id: z.string(),
  start_sec: z.number(),
  end_sec: z.number(),
  mode: z.enum(["single", "dual", "triple", "quad", "screenshare", "wide"]),
  face_count: z.number(),
  face_center_x_pct: z.number().optional(),
  // Single mode: the framed person over the scene; absent = the static face_center_x_pct.
  face_track: z.array(FaceKeyframeSchema).optional(),
  faces: z.array(SceneFaceSchema).optional(),
  layout_check: LayoutCheckSchema.optional(),
  // The user picked `mode` for this scene: Auto keeps it even when it needs more people than were detected.
  user_mode: z.boolean().optional(),
  // Transition into this chunk from the previous one. none/fade/wipe are legacy aliases
  // of cut/crossfade/slide. Absent = the clip default (cut unless default_transition says otherwise).
  transition: z.enum(["cut", "crossfade", "slide", "zoom", "none", "fade", "wipe"]).optional(),
})

export const SceneTransitionSchema = z.object({
  type: z.enum(["cut", "crossfade", "slide", "zoom"]),
  duration_sec: z.number().min(0.1).max(2).optional(),
})

export const SceneMetadataSchema = z.object({
  version: z.string(),
  source: z.object({
    width: z.number(),
    height: z.number(),
    fps: z.number(),
    total_frames: z.number(),
  }),
  chunks: z.array(SceneChunkSchema),
  // "auto" (or absent) = each chunk's own mode; otherwise one layout for the whole clip.
  layout: z.enum(["auto", "single", "dual", "triple", "quad", "screenshare", "wide"]).optional(),
  default_transition: SceneTransitionSchema.optional(),
  // Transitions go only where the layout changes (the default one, or a crossfade); other scene changes cut.
  auto_transitions: z.boolean().optional(),
  speech_segments: z.array(z.object({
    start: z.number(),
    end: z.number(),
  })).optional(),
})

// =============================================================================
// Cuts (editing script 2.1): source-time ranges removed from the output.
// Absent or empty means no cuts. See remotion/utils/cuts.ts for the time mapping.
// =============================================================================

export const CutRangeSchema = z.object({
  startMs: z.number().min(0),
  endMs: z.number().min(0),
})

// =============================================================================
// Editing Script (Root)
// =============================================================================

export const EditingScriptSchema = z.object({
  version: z.string().default("2.0"),
  tracks: z.array(TrackSchema).default([]),
  renderPref: RenderPreferencesSchema.default({}),
  reframeBounds: ReframeBoundsSchema.optional(),
  sceneMetadata: SceneMetadataSchema.optional(),
  cuts: z.array(CutRangeSchema).optional(),
  // Transcript word index -> corrected text (lib/caption-utils applyWordText); timing never changes.
  wordText: z.record(z.string(), z.string()).optional(),
  sourceVideoUrl: z.string().optional(),
  durationMs: z.number().optional(),
  fps: z.number().default(30),
  width: z.number().default(1080),
  height: z.number().default(1920),
  applyWatermark: z.boolean().default(false),
  brandWatermark: z.object({
    type: z.enum(["text", "image"]),
    text: z.string().optional(),
    imageUrl: z.string().optional(),
    position: z.enum(["top-left", "top-right", "bottom-left", "bottom-right", "custom"]),
    x: z.number().min(0).max(100).optional(),
    y: z.number().min(0).max(100).optional(),
    opacity: z.number(),
    fontSize: z.number().optional(),
    fontFamily: z.string().optional(),
    color: z.string().optional(),
  }).nullable().optional(),
})

// =============================================================================
// Type Exports
// =============================================================================

export type TrackType = z.infer<typeof TrackTypeSchema>
export type Section = z.infer<typeof SectionSchema>
export type Track = z.infer<typeof TrackSchema>
export type RenderPreferences = z.infer<typeof RenderPreferencesSchema>
export type ReframeBounds = z.infer<typeof ReframeBoundsSchema>
export type SceneFace = z.infer<typeof SceneFaceSchema>
export type FaceKeyframe = z.infer<typeof FaceKeyframeSchema>
export type SceneChunk = z.infer<typeof SceneChunkSchema>
export type SceneTransition = z.infer<typeof SceneTransitionSchema>
export type SceneMetadata = z.infer<typeof SceneMetadataSchema>
export type AutoHookTextOverlay = z.infer<typeof AutoHookTextOverlaySchema>
export type AutoHook = z.infer<typeof AutoHookSchema>
export type EditingScript = z.infer<typeof EditingScriptSchema>

// =============================================================================
// Helper Track Types
// =============================================================================

export type CaptionTrack = Track & { trackType: "CaptionTrack"; styleConfig: z.infer<typeof CaptionStyleConfigSchema> }
export type HookTrackType = Track & { trackType: "HookTrack" }
export type EmojiTrackType = Track & { trackType: "EmojiTrack" }
export type BRollTrackType = Track & { trackType: "BRollTrack" }
export type MusicTrackType = Track & { trackType: "MusicTrack" }
export type VoiceoverTrackType = Track & { trackType: "VoiceoverTrack" }
export type GifTrackType = Track & { trackType: "GifTrack" }
export type ReframeTrackType = Track & { trackType: "ReframeTrack" }

// =============================================================================
// Default Constants
// =============================================================================

export const DEFAULT_TEXT_STYLE = {
  backgroundColor: "rgba(255,255,255,0.95)",
  fontFamily: "Montserrat",
  fontWeight: "900",
  fontColor: "#000000",
  fontSize: 26,
  italic: false,
  borderRadius: 4,
  padding: 8,
  textAlign: "center",
} as const

export const DEFAULT_POSITION = {
  type: "CPct" as const,
  xPct: 0,
  yPct: 0,
  wPct: 0.9,
  hPct: 0.1,
}

export const DEFAULT_CAPTION_STYLE_CONFIG = {
  font: { family: "Montserrat", size: 54, weight: "900", style: "normal" },
  colors: { default: "#FFFFFF", primary: "#04F827", secondary: "#FFFD03" },
  stroke: { enabled: true, color: "#000000", width: 7 },
  shadow: { enabled: true, color: "#000000", x: 2, y: 2, blur: 3 },
  backdrop: { enabled: false, color: "rgba(0,0,0,0.55)", paddingX: 16, paddingY: 6, radius: 14 },
  animation: "karaoke",
  textTransform: "uppercase",
  position: "bottom",
  positionOffset: 10,
  wordsPerCaption: 5,
  activeWordEnabled: true,
  lineHeight: 1.15,
} as const

export const DEFAULT_RENDER_PREFERENCES: RenderPreferences = {
  enableCaption: true,
  enableBroll: true,
  enableHook: true,
  enableEmoji: false,
  enableMusic: false,
  enableVoiceover: true,
  enableGif: false,
  enableReframe: false,
  layoutAspectRatio: "portrait",
}
